import React from 'react';
import { FaCheck, FaMinus } from 'react-icons/fa';
import './Checkbox.css';

// ─── Checkbox ───────────────────────────────────────────────────────────────
// The site's checkbox: a toggle button (role="checkbox") drawn as a box with a
// tick, used instead of <input type="checkbox">. Being a button, it isn't caught
// by the global input styles (App.css gives every input width: 100%, which
// stretched native checkboxes across their row).
//
// It behaves like the native input: a click or Space toggles it; inside a
// <label>, clicking the label's text toggles it too; and onChange gets an
// event-like object whose target has { name, type: 'checkbox', checked, value }
// (checked = the new state), so handlers written for the input -- e.g. ones
// reading e.target.checked, or `const { name, type, checked } = e.target` --
// work unchanged.
//
// Props:
//   checked       – whether it's ticked
//   onChange      – (event) => void, as above
//   indeterminate – shows a dash instead (e.g. "not set"); clicking ticks it
//   label         – optional text/node shown after the box (part of the button)
//   size          – 'sm' (14px) | 'md' (16px, default) | 'lg' (20px)
//   disabled, name, value, id, title, className, onClick, aria-label, tabIndex
export default function Checkbox({
  checked = false,
  onChange,
  indeterminate = false,
  label,
  size = 'md',
  disabled = false,
  name,
  value,
  id,
  title,
  className,
  onClick,
  tabIndex,
  'aria-label': ariaLabel,
}) {
  const handleClick = (e) => {
    onClick?.(e);
    if (disabled || e.defaultPrevented) return;
    const target = { name, type: 'checkbox', checked: !checked, value };
    onChange?.({
      target,
      currentTarget: target,
      nativeEvent: e.nativeEvent,
      preventDefault: () => e.preventDefault(),
      stopPropagation: () => e.stopPropagation(),
    });
  };

  const classes = [
    'checkbox',
    `checkbox--${size}`,
    checked ? 'is-checked' : '',
    indeterminate && !checked ? 'is-indeterminate' : '',
    className || '',
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate && !checked ? 'mixed' : checked}
      aria-label={ariaLabel}
      id={id}
      name={name}
      title={title}
      tabIndex={tabIndex}
      disabled={disabled}
      className={classes}
      onClick={handleClick}
    >
      <span className="checkbox-box" aria-hidden="true">
        {checked ? <FaCheck /> : (indeterminate ? <FaMinus /> : null)}
      </span>
      {label != null && label !== false && <span className="checkbox-label">{label}</span>}
    </button>
  );
}
